<template>
	<v-chart
		class="chart"
		:option="option"
		ref="chart"
		:init-options="{ renderer: 'svg' }"
		autoresize
	/>
</template>




<script>
// https://github.com/ecomfe/vue-echarts?tab=readme-ov-file
// https://echarts.apache.org/handbook/en/concepts/dataset/#
import VChart from 'vue-echarts';
import * as echarts from 'echarts';
import { useStationsStore } from '@/stores/StationsStore.js'
import { useStationsHistoryStore } from '@/stores/StationsHistoryStore.js'
import { INFLUXDB } from '@/api/api.js'
import { Station } from '@/models/station.js'
import { ServiceUnit } from '@/models/serviceUnit.js'
import {
	chartDateStr,
	firstYearDay,
	lastYearDay,
} from '@/helpers.js'

import { use } from 'echarts/core'
import {
	HeatmapChart
} from 'echarts/charts'
import {
	TitleComponent,
	TooltipComponent,
	VisualMapComponent,
	CalendarComponent
} from 'echarts/components'
import {
	CanvasRenderer
} from 'echarts/renderers'

use([
	TitleComponent,
	TooltipComponent,
	VisualMapComponent,
	CalendarComponent,
	HeatmapChart,
	CanvasRenderer
])


export default {
	GREEN: '#73bf69',
	YELLOW: '#ffee52',
	RED: '#f2495c',
	BLUE: '#5794f2',
	CIRCUIT_ERROR_CODE: 99999,

	components: {
		VChart,
	},


	props: {
		stationId: {
			type: String,
			require: true,
		},
		metricName: {
			type: String,
			require: true,
		},
		// Year for chow in heatmap
		// For update current year just togle year as Number and as String
		year: {
			type: [Number, String],
			require: true,
		},
	},


	emits: {
		selectedDay: value => typeof value === "string",
	},


	setup() {
		const stationsStore = useStationsStore()
		const historyStore = useStationsHistoryStore()

		return {
			stationsStore,
			historyStore,
			Station,
			INFLUXDB,
		}
	},


	data() {
		return {
			// Graph configuration
			option: {
				title: {
					top: 0,
					left: 'center',
					text: 'Відкриття корпусу СКЗ'
				},
				tooltip: {
					// Tooltip builder function
					formatter: (params/*, ticket, callback*/) => {
						const date = new Date(params.value[0])
						const isNoCircuitData = isNaN(params.value[1])
						const isNoConnectionData = isNaN(params.value[3])

						let tooltip = `<div class="text-center">`
							// Addtooltip header
							tooltip += `${chartDateStr(date)}`
						tooltip += `</div>`

						if (!isNoCircuitData) {
							const isOpend = Number(params.value[1]) > 0
							const isCircuitError = params.value[1] == this.$options.CIRCUIT_ERROR_CODE

							tooltip += `<div class="text-left">`
								if (isCircuitError) {
									tooltip += `Двері: <span style="color: ${this.$options.YELLOW};"> помилка в контурі</span>`
								} else {
									tooltip += `Двері: ${isOpend ? '' : 'не'} <span style="color: ${isOpend ? this.$options.RED : 'inherit'};"> відкривались</span>`
								}
							tooltip += `</div>`
						}

						if (!isNoConnectionData) {
							tooltip += `<div class="text-left">`
								tooltip += `Наявність зв'язку: ${params.value[3]} %`
							tooltip += `</div>`
						}

						return tooltip
					},
				},
				visualMap: {
					type: 'piecewise',
					dimension: 1, // get value fron data[1]
					orient: 'horizontal',
					left: 'center',
					top: 40,
					controller: {
						inRange: {
							opacity: [0.3, 0.6]
						},
					},
					pieces: [
						// {
						// 	value: -1,
						// 	label: 'Даних немає    ',
						// 	color: this.$options.BLUE,
						// colorAlpha: 0.7,
						// },
						{
							value: 0,
							label: 'Двері закриті    ',
							color: this.$options.GREEN,
							colorAlpha: 0.8,
						},
						{
							min: 1,
							max: this.$options.CIRCUIT_ERROR_CODE - 1,
							label: 'Двері відкривались за добу    ',
							color: this.$options.RED,
							colorAlpha: 0.8,
						},
						{
							value: this.$options.CIRCUIT_ERROR_CODE,
							label: 'Були помилки за добу    ',
							color: this.$options.YELLOW,
							colorAlpha: 0.8,
						},
					],
					seriesIndex: 0,
				},
				// Calendar configuration
				calendar: {
					top: 100,
					left: 40,
					right: 15,
					cellSize: ['auto', 20],
					range: '2025',
					itemStyle: {
						borderWidth: 1,
					},
					yearLabel: {
						show: false
					},
					monthLabel: {
						nameMap: ['Січ', 'Лют', 'Бер', 'Кві', 'Тра', 'Чер', 'Лип', 'Сер', 'Вер', 'Жов', 'Лис', 'Гру'],
					},
					dayLabel: {
						nameMap: ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
						firstDay: 1 // start on Monday
					},
				},
				// Data series
				series: {
					// Custom day cell render
					type: 'custom',
					coordinateSystem: 'calendar',
					selectedMode: 'single',
					// silent: true,
					renderItem: (params, api) => {
						const borderColor = '#ccc'
						const item = {
							type: 'group',
							children: [],
						}

						const point = api.coord([api.value(0), 0]) // day coordinates
						if (!point[0]) {
							return
						}
						// Fix cell size
						const size = [params.coordSys.cellWidth, params.coordSys.cellHeight]

						// If no circuit data for all day
						if (isNaN(api.value(1))
						) {
							// Background for clicability
							const background = {
								type: 'rect',
								shape: {
									x: point[0] - size[0] / 2,
									y: point[1] - size[1] / 2,
									width: size[0],
									height: size[1],
								},
								style: {
									// Transparent background
									fill: 'rgba(255, 255, 255, 0)',
									stroke: borderColor,
									lineWidth: 1,
								},
								select: {
									style: {
										stroke: '#000',
										lineWidth: 2,
									},
								},
							}

							const crossBorder = 3
							const crossColor = '#ddd'
							// Make cross
							const noDataLine1 = {
								type: 'line',
								shape: {
									x1: point[0] - size[0] / 2 + crossBorder,
									y1: point[1] - size[1] / 2 + crossBorder,
									x2: point[0] + size[0] / 2 - crossBorder,
									y2: point[1] + size[1] / 2 - crossBorder,
								},
								style: {
									stroke: crossColor,
									lineWidth: 1.5,
								},
							}
							const noDataLine2 = {
								type: 'line',
								shape: {
									x1: point[0] - size[0] / 2 + crossBorder,
									y1: point[1] + size[1] / 2 - crossBorder,
									x2: point[0] + size[0] / 2 - crossBorder,
									y2: point[1] - size[1] / 2 + crossBorder,
								},
								style: {
									stroke: crossColor,
									lineWidth: 1.5,
								},
							}

							item.children.push(background)
							item.children.push(noDataLine1)
							item.children.push(noDataLine2)
						} else {
							let circuitStateColor = borderColor // by default

							// If door has been opened
							if (api.value(1) > 0 && api.value(1) < this.$options.CIRCUIT_ERROR_CODE) {
								circuitStateColor = this.$options.RED
							// If no door open states
							} else if (api.value(1) == 0) {
								circuitStateColor = this.$options.GREEN
							// If error in circuit
							} else if (api.value(1) == this.$options.CIRCUIT_ERROR_CODE) {
								circuitStateColor = this.$options.YELLOW
							} else {
								circuitStateColor = this.$options.GREY
							}
						
							item.children.push({
								type: 'rect',
								shape: {
									x: point[0] - size[0] / 2,
									y: point[1] - size[1] / 2,
									width: size[0],
									height: size[1],
								},
								style: {
									fill: circuitStateColor,
									stroke: borderColor,
									lineWidth: 2,
								},
								select: {
									style: {
										stroke: '#000',
										lineWidth: 2,
									},
								},
							})
		
							// if (this.dateToKey(new Date(api.value(0))) == '2025-05-16') {
							// 	console.log(`day data ${this.dateToKey(new Date(api.value(0)))}, opening=${api.value(1)}, ${api.value(2)}, connection=${api.value(3)}%, ${api.value(4)}`)
							// 	// debugger
							// }
							
							// If need to show connection
							if (api.value(2) == '0'
								&& Number(api.value(3)) < 90
								&& api.value(1) != this.$options.CIRCUIT_ERROR_CODE
							) {
								const iconPadding = 3
								const viewBoxSize = 16
								const iconSize = Math.min(...size) - iconPadding * 2
								const iconScale = iconSize / viewBoxSize
								item.children.push({
									type: 'path',
									shape: {
										// d: 'M240-40q-50 0-85-35t-35-85q0-50 35-85t85-35q14 0 26 3t23 8l57-71q-28-31-39-70t-5-78l-81-27q-17 25-43 40t-58 15q-50 0-85-35T0-580q0-50 35-85t85-35q50 0 85 35t35 85v8l81 28q20-36 53.5-61t75.5-32v-87q-39-11-64.5-42.5T360-840q0-50 35-85t85-35q50 0 85 35t35 85q0 42-26 73.5T510-724v87q42 7 75.5 32t53.5 61l81-28v-8q0-50 35-85t85-35q50 0 85 35t35 85q0 50-35 85t-85 35q-32 0-58.5-15T739-515l-81 27q6 39-5 77.5T614-340l57 70q11-5 23-7.5t26-2.5q50 0 85 35t35 85q0 50-35 85t-85 35q-50 0-85-35t-35-85q0-20 6.5-38.5T624-232l-57-71q-41 23-87.5 23T392-303l-56 71q11 15 17.5 33.5T360-160q0 50-35 85t-85 35ZM120-540q17 0 28.5-11.5T160-580q0-17-11.5-28.5T120-620q-17 0-28.5 11.5T80-580q0 17 11.5 28.5T120-540Zm120 420q17 0 28.5-11.5T280-160q0-17-11.5-28.5T240-200q-17 0-28.5 11.5T200-160q0 17 11.5 28.5T240-120Zm240-680q17 0 28.5-11.5T520-840q0-17-11.5-28.5T480-880q-17 0-28.5 11.5T440-840q0 17 11.5 28.5T480-800Zm0 440q42 0 71-29t29-71q0-42-29-71t-71-29q-42 0-71 29t-29 71q0 42 29 71t71 29Zm240 240q17 0 28.5-11.5T760-160q0-17-11.5-28.5T720-200q-17 0-28.5 11.5T680-160q0 17 11.5 28.5T720-120Zm120-420q17 0 28.5-11.5T880-580q0-17-11.5-28.5T840-620q-17 0-28.5 11.5T800-580q0 17 11.5 28.5T840-540ZM480-840ZM120-580Zm360 120Zm360-120ZM240-160Zm480 0Z',
										d: 'M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.15.15 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.2.2 0 0 1-.054.06.1.1 0 0 1-.066.017H1.146a.1.1 0 0 1-.066-.017.2.2 0 0 1-.054-.06.18.18 0 0 1 .002-.183L7.884 2.073a.15.15 0 0 1 .054-.057m1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z',
										// d: 'M7.022 1.566a1.13 1.13 0 0 1 1.96 0l6.857 11.667c.457.778-.092 1.767-.98 1.767H1.144c-.889 0-1.437-.99-.98-1.767z'
										// d: 'M10.706 3.294A12.6 12.6 0 0 0 8 3C5.259 3 2.723 3.882.663 5.379a.485.485 0 0 0-.048.736.52.52 0 0 0 .668.05A11.45 11.45 0 0 1 8 4q.946 0 1.852.148zM8 6c-1.905 0-3.68.56-5.166 1.526a.48.48 0 0 0-.063.745.525.525 0 0 0 .652.065 8.45 8.45 0 0 1 3.51-1.27zm2.596 1.404.785-.785q.947.362 1.785.907a.482.482 0 0 1 .063.745.525.525 0 0 1-.652.065 8.5 8.5 0 0 0-1.98-.932zM8 10l.933-.933a6.5 6.5 0 0 1 2.013.637c.285.145.326.524.1.75l-.015.015a.53.53 0 0 1-.611.09A5.5 5.5 0 0 0 8 10m4.905-4.905.747-.747q.886.451 1.685 1.03a.485.485 0 0 1 .047.737.52.52 0 0 1-.668.05 11.5 11.5 0 0 0-1.811-1.07M9.02 11.78c.238.14.236.464.04.66l-.707.706a.5.5 0 0 1-.707 0l-.707-.707c-.195-.195-.197-.518.04-.66A2 2 0 0 1 8 11.5c.374 0 .723.102 1.021.28zm4.355-9.905a.53.53 0 0 1 .75.75l-10.75 10.75a.53.53 0 0 1-.75-.75z'
									},
									// Move icon to cell center
									x: point[0] - iconSize / 2,
									y: point[1] - iconSize / 2,
									style: {
										fill: Number(api.value(3)) < 10 ? this.$options.RED : this.$options.YELLOW,
										stroke: Number(api.value(3)) < 10 ? this.$options.RED : this.$options.YELLOW,
										lineWidth: 0.8,
										color: Number(api.value(3)) < 10 ? this.$options.RED : this.$options.YELLOW,
									},
									scaleX: iconScale, // Масштабування під розмір клітинки
									scaleY: iconScale,
								})
							}
						}

						return item
					},
					data: [],
				},
			},
		}
	},

	watch: {
		// Update chart on year update
		async year(newYear, /*oldYear*/) {
			// console.log('update year', newYear, oldYear)
			// Show loading animation
			this.chartInstance.showLoading('default', {
				text: '',
				color: '#5470C6',
				textColor: '#000',
				maskColor: 'rgba(255, 255, 255, 0.8)',
				zlevel: 0,
			})

			this.option.calendar.range = this.year

			await this.historyStore.loadMetrics(
				this.metricsId,
				[
					[INFLUXDB.station_metrics.service_unit, ServiceUnit.metricKeys.fSecurityCircuitBoxDoor],
					[INFLUXDB.station_metrics.service_unit, ServiceUnit.metricKeys.isConnect],
				],
				firstYearDay(newYear),
				lastYearDay(newYear),
				true,
			) 

			this.updateChart()

			// Hide loading animation
			this.chartInstance.hideLoading()
		},

		updateTriger() {
			this.updateChart()
		},
	},

	created() {
		this.option.calendar.range = this.year
	},

	mounted() {
		this.updateChart()

		// Set current day as selected to trigger chart update
		this.$nextTick(() => this.selectDay(this.dateToKey(new Date())))
	},

	methods: {
		updateChart() {
			const mInDay = 1000 * 60 * 60 * 24
			
			// console.log('Update chart ', this.year)		
			const startDate = new Date(`${this.year}-01-01`)
			let endDate
			// If filling current year
			if (this.year == new Date().getFullYear()) {
				endDate = new Date()
			} else {
				endDate = new Date(`${this.year}-12-31`)
			}

			// Initialize calendar for current year
			let calendarData = this.fillAllDaysByDefault(startDate, endDate)

			// Fill circuit state
			const circuitMetrics = this.historyStore.getMetricHistory(
				this.metricsId,
				INFLUXDB.station_metrics.service_unit,
				this.metricName,
				startDate,
				endDate,
			)
			
			for (const metric of circuitMetrics) {
				const dayCode = this.dateToKey(metric.time)
				
				// If no such day in data Object
				if (!calendarData[dayCode]) {
					continue
				}

				// If no circuit data for day
				if (calendarData[dayCode][1] === undefined) {
					calendarData[dayCode][1] = 0
				}

				switch(parseInt(metric.value)) {
					case ServiceUnit.boxDoorStateCodes.open:
						// If it was error in day
						if (calendarData[dayCode][1] == this.$options.CIRCUIT_ERROR_CODE) {
							calendarData[dayCode][1] = 0
						}
						calendarData[dayCode][1]++
						break
					case ServiceUnit.boxDoorStateCodes.short:
						// If close all day (in this moment)
						if(calendarData[dayCode][1] == 0) {
							calendarData[dayCode][1] = this.$options.CIRCUIT_ERROR_CODE
						}
						break
				}
			}

			// Fill connection state (states sorted by time)
			const connectionMetrics = this.historyStore.getMetricHistory(
				this.metricsId,
				INFLUXDB.station_metrics.service_unit,
				ServiceUnit.metricKeys.isConnect,
				startDate,
				endDate,
			)

			// console.log('Connection metrics ', connectionMetrics)
			let curDayCode = null
			let curDayTime = null
			let connectedPeriod = 0
			let disconnectedPeriod = 0
			for (const metric of connectionMetrics) {
				const dayCode = this.dateToKey(metric.time)

				// If no such day in data Object
				if (!calendarData[dayCode]) {
					continue
				}

				// Remember current day
				if (curDayCode === null || curDayCode != dayCode) {
					curDayCode = dayCode
					connectedPeriod = 0
					disconnectedPeriod = 0
					curDayTime = metric.time.getTime()
				}

				// Calculate connection percent
				if (metric.value) {
					connectedPeriod += metric.time.getTime() - curDayTime
				} else {
					disconnectedPeriod += metric.time.getTime() - curDayTime
				}
				curDayTime = metric.time.getTime()
				const now = new Date()
				const isToday = metric.time.getFullYear() == now.getFullYear()
					&& metric.time.getMonth() == now.getMonth()
					&& metric.time.getDate() == now.getDate()
				const dayStart = new Date(`${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}T00:00:00.000Z`)
				dayStart.setHours(0)
				const divider = isToday ? now.getTime() - dayStart.getTime() : mInDay
				// Save connection percents
				calendarData[dayCode][3] = Number(connectedPeriod / divider * 100).toFixed(0)
				calendarData[dayCode][4] = Number(disconnectedPeriod / divider * 100).toFixed(0)

				// If no connection data for day
				if (calendarData[dayCode][2] === undefined) {	
					calendarData[dayCode][2] = metric.value ? true : false
				}

				// If connection is OK but new connection metric is "no connection"
				if (calendarData[dayCode][2] == true && !metric.value) {
					calendarData[dayCode][2] = false
				}
			}

			this.option.series.data = []
			this.option.series.data = Object.values(calendarData)

			this.setClickOnDayHandler()
		},

		fillAllDaysByDefault(startDate, endDate) {
			const days = {}

			for (var d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
				const key = this.dateToKey(d)
				// Init day data: door state = undefined, connection = undefined
				days[key] = [key, undefined, undefined, undefined, undefined]
			}

			return days
		},

		dateToKey(date) {
			return echarts.time.format(date, '{yyyy}-{MM}-{dd}', false)
		},

		setClickOnDayHandler() {
			this.chartInstance.off('click')

			// Set handler for click on day
			this.chartInstance.on('click', (params) => {
				this.selectDay(params.value[0], params.seriesIndex, params.dataIndex)
			})
		},

		// Selects given day on the calendar and notifies the parent
		selectDay(date, seriesIndex, dataIndex) {
			// Find cell index by date if it was not provided (e.g. initial selection)
			if (dataIndex === undefined) {
				dataIndex = this.option.series.data.findIndex(dayData => dayData[0] === date)
				seriesIndex = 0
			}

			if (dataIndex === -1) {
				return
			}

			// Unselect previously selected day and select the new one
			this.chartInstance.dispatchAction({ type: 'unselect', seriesIndex: 0 })
			this.chartInstance.dispatchAction({
				type: 'select',
				seriesIndex,
				dataIndex,
			})

			this.$emit('selectedDay', date)
		},
	},


	computed: {
		station() { return this.stationsStore.station(this.stationId) },

		metricsId() { return this.station.passport.get(Station.passportKeys.metricsId) },

		chartInstance() { return this.$refs.chart?.chart },

		updateTriger() { return this.historyStore.updateTriger },
	},
}
</script>




<style scoped>
</style>